/**
 * The room's state as a pure reducer.
 *
 * The page never refetches the room on a websocket event: an `item` event is
 * upserted by id, a `tag` attaches to its item, `run`/`persona` replace their
 * row, and the rail's counts are bumped in place. All of that is here, with no
 * React and no router, so it can be tested under CRA's jest.
 *
 * Mirrors `jcseo/boardroom/filters.py` — keep SAVED_FILTERS in step.
 */
import type {
  BoardroomDetail,
  BoardroomItem,
  BoardroomItemKind,
  BoardroomLookupCounts,
  BoardroomLookupState,
  BoardroomPersona,
  BoardroomTag,
  BoardroomVerdictRef,
  BoardroomWebSocketEvent,
} from '../../types';
import { LOOKUP_KINDS } from './kinds';

export interface SavedFilter {
  key: string;
  label: string;
  kinds: BoardroomItemKind[];
}

export const SAVED_FILTERS: SavedFilter[] = [
  // The same list as the server's (jcseo/boardroom/filters.py), in its order.
  { key: 'understand', label: 'Understand', kinds: ['NOTE', 'EVIDENCE', 'OBSERVATION'] },
  { key: 'questions', label: 'Questions', kinds: ['QUESTION'] },
  { key: 'hunches', label: 'Hunches', kinds: ['HUNCH'] },
  { key: 'solution_types', label: 'Solution types', kinds: ['SOLUTION_TYPE'] },
  { key: 'companies', label: 'Companies', kinds: ['COMPANY'] },
  { key: 'products', label: 'Products', kinds: ['PRODUCT'] },
  { key: 'features', label: 'Features', kinds: ['FEATURE'] },
  { key: 'pricing', label: 'Pricing', kinds: ['PRICING'] },
  { key: 'videos', label: 'Videos', kinds: ['VIDEO'] },
  { key: 'pains', label: 'Pains', kinds: ['PAIN'] },
  { key: 'story', label: 'Story', kinds: ['PERSON', 'EVENT', 'ANGLE'] },
  { key: 'proposals', label: 'Proposals', kinds: ['IDEA'] },
  { key: 'review', label: 'Review', kinds: ['CRITIQUE', 'VERDICT'] },
  { key: 'lookups', label: 'Lookups', kinds: LOOKUP_KINDS },
];

export function kindsForFilter(key: string | null): BoardroomItemKind[] | null {
  if (!key) return null;
  return SAVED_FILTERS.find(f => f.key === key)?.kinds ?? null;
}

export function matchesFilter(item: BoardroomItem, key: string | null): boolean {
  const kinds = kindsForFilter(key);
  return kinds === null ? true : kinds.includes(item.kind);
}

export function parseTag(tag: string | null): { dimension: string; value: string } | null {
  if (!tag) return null;
  const i = tag.indexOf(':');
  if (i < 0) return null;
  return { dimension: tag.slice(0, i).trim().toUpperCase(), value: tag.slice(i + 1).trim().toLowerCase() };
}

export function matchesTag(item: BoardroomItem, tag: string | null): boolean {
  const t = parseTag(tag);
  if (!t) return true;
  return item.tags.some(x => x.dimension === t.dimension && x.value === t.value);
}

/** One term's alternatives, in its key: "CONTENT:user complaint|pricing". */
export const TERM_OR = '|';
/** A NOT term's key starts with this: "!PRODUCT_FUNCTION:crm". */
export const TERM_NOT = '!';

export interface TagTerm {
  /** The term as the lens stores it: "CONTENT:user complaint|pricing". */
  key: string;
  dimension: string;
  /** Any of these will do - or, for a NOT term, none of them. */
  values: string[];
  /** Sorted on the dimension, and none of `values`. */
  negated: boolean;
}

/** The lens's tags as TERMS, every one of which holds; a term's values are
 *  alternatives. "CONTENT:user complaint" is a term of one, which is every
 *  key a link or saved view held before terms had alternatives. Pure. */
export function tagTerms(tags: string[]): TagTerm[] {
  const out: TagTerm[] = [];
  for (const key of tags) {
    const negated = key.startsWith(TERM_NOT);
    const p = parseTag(negated ? key.slice(TERM_NOT.length) : key);
    if (!p) continue;
    const values = p.value.split(TERM_OR).map(v => v.trim()).filter(Boolean);
    if (values.length && !out.some(t => t.key === key)) out.push({ key, dimension: p.dimension, values, negated });
  }
  return out;
}

const termKey = (dimension: string, values: string[], negated = false) =>
  `${negated ? TERM_NOT : ''}${dimension}:${values.join(TERM_OR)}`;

/** Whether "DIM:value" is in the lens, in any term. Pure. */
export function tagPicked(tags: string[], key: string): boolean {
  const p = parseTag(key);
  return !!p && tagTerms(tags).some(t => t.dimension === p.dimension && t.values.includes(p.value));
}

/** Whether "DIM:value" is in the lens only as "is not": the Tags tab draws
 *  it amber, as the lens bar does, not as a value being looked for. Pure. */
export function tagNegated(tags: string[], key: string): boolean {
  const p = parseTag(key);
  if (!p) return false;
  const holding = tagTerms(tags).filter(t => t.dimension === p.dimension && t.values.includes(p.value));
  return holding.length > 0 && holding.every(t => t.negated);
}

/** The tag lens: EVERY term, each by ANY of its values - "(content: user
 *  complaint or pricing) and product function: data analytics". */
export function matchesTags(item: BoardroomItem, tags: string[]): boolean {
  return tagTerms(tags).every(term => {
    const any = term.values.some(v => matchesTag(item, `${term.dimension}:${v}`));
    if (!term.negated) return any;
    // NOT: sorted on the dimension, and none of these - an item nothing says
    // anything about on it is not "not a CRM".
    return !any && item.tags.some(x => x.dimension === term.dimension);
  });
}

/** A tag clicked in the Tags drawer: in the lens anywhere, it comes off;
 *  otherwise it is ONE MORE TERM, so it narrows, in its own category too.
 *  Alternatives ("or") are ticked into a term in the lens bar (`setTermValues`). */
export function toggleTag(tags: string[], key: string): string[] {
  const p = parseTag(key);
  if (!p || !tagPicked(tags, key)) return [...tags, key];
  return tags.flatMap(k => {
    const term = tagTerms([k])[0];
    if (!term || term.dimension !== p.dimension || !term.values.includes(p.value)) return [k];
    const rest = term.values.filter(v => v !== p.value);
    return rest.length ? [termKey(term.dimension, rest, term.negated)] : [];
  });
}

/** One term's values set to `values` (any of them), in place; none takes the
 *  term off. Pure. */
export function setTermValues(tags: string[], key: string, values: string[]): string[] {
  const term = tagTerms([key])[0];
  if (!term) return tags;
  return tags.flatMap(k =>
    k === key ? (values.length ? [termKey(term.dimension, values, term.negated)] : []) : [k],
  );
}

/** One term flipped between "is" and "is not", in place. Pure. */
export function setTermNegated(tags: string[], key: string, negated: boolean): string[] {
  const term = tagTerms([key])[0];
  if (!term) return tags;
  return tags.map(k => (k === key ? termKey(term.dimension, term.values, negated) : k));
}

export function withoutTag(tags: string[], key: string): string[] {
  return tags.filter(t => t !== key);
}

export function tagsLabel(tags: string[]): string {
  return tags.map(t => t.toLowerCase()).join(' · ');
}

export type VerifiedFilter = 'HOLDS' | 'DOES_NOT_HOLD' | 'UNVERIFIABLE' | 'UNCHECKED';

export const VERIFIED_OPTIONS: { key: VerifiedFilter; label: string }[] = [
  { key: 'HOLDS', label: 'confident' },
  { key: 'DOES_NOT_HOLD', label: 'not confident' },
  { key: 'UNCHECKED', label: 'not validated' },
  { key: 'UNVERIFIABLE', label: 'unverifiable' },
];

export type SourcedFilter = 'UNSOURCED' | 'SOURCED';

/** The kinds that never carry a receipt: lookups are receipts, rulings are about something else. */
const UNSOURCEABLE: ReadonlySet<string> = new Set(['LOOKUP_SERP', 'LOOKUP_PAGE', 'LOOKUP_DEMAND', 'LOOKUP_TRENDS', 'LOOKUP_NEWS', 'LOOKUP_SOCIAL', 'LOOKUP_CONSOLE', 'VERDICT', 'CRITIQUE']);

/** A persona wrote it and nothing on the board says from what. Mirrors `items.is_unsourced`. */
export function isUnsourced(item: BoardroomItem): boolean {
  if (item.created_by_persona_id == null) return false;
  if (UNSOURCEABLE.has(item.kind)) return false;
  const ids = item.payload?.receipt_ids;
  return !(Array.isArray(ids) && ids.some(Boolean));
}

export function matchesSourced(item: BoardroomItem, sourced: SourcedFilter | null | undefined): boolean {
  if (!sourced) return true;
  return sourced === 'UNSOURCED' ? isUnsourced(item) : !isUnsourced(item);
}

export function matchesVerified(item: BoardroomItem, verified: VerifiedFilter | null): boolean {
  if (!verified) return true;
  if (verified === 'UNCHECKED') return item.verdict === null;
  return item.verdict?.verdict === verified;
}

/** "By whom": a persona id as a string, `'human'` for your own items, or null. */
export type ByFilter = string | null;

export function matchesBy(item: BoardroomItem, by: ByFilter): boolean {
  if (!by) return true;
  if (by === 'human') return item.created_by_persona_id === null;
  return String(item.created_by_persona_id) === by;
}

/** Lookup states, in the order the rail lists them. Mirrors
 *  `items.LOOKUP_STATES` / `LOOKUP_STATE_LABELS` on the backend. */
export const LOOKUP_STATE_OPTIONS: { key: BoardroomLookupState; label: string }[] = [
  { key: 'landed', label: 'waiting to be read' },
  // Still out, in the order a lookup moves through them.
  { key: 'waiting_to_search', label: 'waiting to search' },
  { key: 'searching', label: 'searching' },
  { key: 'waiting_to_load', label: 'waiting to load page' },
  { key: 'loading', label: 'loading page' },
  { key: 'waiting_for_data', label: 'waiting for data' },
  { key: 'getting_data', label: 'getting data' },
  { key: 'parsing', label: 'parsing' },
  { key: 'in_console', label: 'waiting for you in the console' },
  { key: 'failed', label: 'failed' },
  { key: 'not_fetched', label: 'not fetched (the dig ended)' },
  { key: 'read', label: 'read' },
];

export const EMPTY_LOOKUP_COUNTS: BoardroomLookupCounts = {
  queued: 0,
  fetching: 0,
  waiting_to_search: 0,
  searching: 0,
  waiting_to_load: 0,
  loading: 0,
  waiting_for_data: 0,
  getting_data: 0,
  parsing: 0,
  in_console: 0,
  landed: 0,
  read: 0,
  failed: 0,
  not_fetched: 0,
};

/** Where one lookup item is: `woke` is stamped false when a result lands and
 *  true once a persona's brief has shown it, so DONE splits into landed/read. */
/** What a lookup the dig ended before it ran says, word for word. */
export const DIG_ENDED = 'the dig ended before this was fetched';

export function lookupState(item: BoardroomItem): BoardroomLookupState | null {
  if (!LOOKUP_KINDS.includes(item.kind)) return null;
  switch (item.status) {
    case 'OPEN':
    case 'RESERVED':
    case 'IN_PROGRESS': {
      // The server says where it is; a live event does not carry that, so
      // fall back on what the item's own status can tell.
      if (item.lookup_stage) return item.lookup_stage;
      const moving = item.status !== 'OPEN';
      if (item.kind === 'LOOKUP_SERP') return moving ? 'searching' : 'waiting_to_search';
      if (item.kind === 'LOOKUP_PAGE') return moving ? 'loading' : 'waiting_to_load';
      if (item.kind === 'LOOKUP_CONSOLE') return 'in_console';
      return moving ? 'getting_data' : 'waiting_for_data';
    }
    case 'DONE':
      return item.payload.woke === false ? 'landed' : 'read';
    case 'FAILED':
      // The server's words for "the dig ended before this was fetched" (readiness.DIG_ENDED).
      return item.error === DIG_ENDED ? 'not_fetched' : 'failed';
    default:
      return null;
  }
}

export function matchesLookup(item: BoardroomItem, lookup: BoardroomLookupState | null): boolean {
  if (!lookup) return true;
  // The two totals, for a filter picked before the stages existed.
  if (lookup === 'queued') return LOOKUP_KINDS.includes(item.kind) && item.status === 'OPEN';
  if (lookup === 'fetching')
    return LOOKUP_KINDS.includes(item.kind) && (item.status === 'RESERVED' || item.status === 'IN_PROGRESS');
  return lookupState(item) === lookup;
}

export interface View {
  filter: string | null;
  /** "DIM:value" keys: any of a dimension's, and every dimension. */
  tags: string[];
  verified: VerifiedFilter | null;
  /** UNSOURCED = persona items with no receipt; SOURCED = everything else.
   *  Optional so a view built elsewhere (tests, older callers) still type-checks; absent = off. */
  sourced?: SourcedFilter | null;
  by: ByFilter;
  /** One lookup state; only meaningful with the lookups bucket. */
  lookup: BoardroomLookupState | null;
  /** One lookup kind (search, page, demand, trends); only with the lookups bucket. */
  kind: BoardroomItemKind | null;
}

export const EMPTY_VIEW: View = { filter: null, tags: [], verified: null, sourced: null, by: null, lookup: null, kind: null };

export function viewIsSet(view: View): boolean {
  return Boolean(view.filter || view.tags.length || view.verified || view.sourced || view.by || view.lookup || view.kind);
}

export function matchesKind(item: BoardroomItem, kind: BoardroomItemKind | null): boolean {
  return kind === null || item.kind === kind;
}

export function matchesView(item: BoardroomItem, view: View): boolean {
  return (
    matchesFilter(item, view.filter) &&
    matchesTags(item, view.tags) &&
    matchesVerified(item, view.verified) &&
    matchesSourced(item, view.sourced) &&
    matchesBy(item, view.by) &&
    matchesLookup(item, view.lookup) &&
    matchesKind(item, view.kind)
  );
}

export interface RoomState {
  room: BoardroomDetail;
  /** The feed for the current view, ascending by id. */
  items: BoardroomItem[];
}

/** Insert or replace by id, keeping ascending id order. */
export function upsertItem(items: BoardroomItem[], item: BoardroomItem): BoardroomItem[] {
  const at = items.findIndex(i => i.id === item.id);
  if (at >= 0) {
    const next = items.slice();
    next[at] = item;
    return next;
  }
  // Almost always the newest, so scan from the end.
  let pos = items.length;
  while (pos > 0 && items[pos - 1].id > item.id) pos -= 1;
  return [...items.slice(0, pos), item, ...items.slice(pos)];
}

function withCounts(room: BoardroomDetail, counts: Record<string, number>): BoardroomDetail {
  return {
    ...room,
    item_counts: counts,
    filters: SAVED_FILTERS.map(f => ({
      key: f.key,
      label: f.label,
      count: f.kinds.reduce((n, k) => n + (counts[k] || 0), 0),
    })),
  };
}

/** A brand-new item bumps its kind's count (and the filter that holds it). */
export function bumpCounts(room: BoardroomDetail, item: BoardroomItem): BoardroomDetail {
  const counts = { ...room.item_counts, [item.kind]: (room.item_counts[item.kind] || 0) + 1 };
  return { ...withCounts(room, counts), items_created: room.items_created + 1, last_item_id: item.id };
}

function attachTag(item: BoardroomItem, tag: BoardroomTag): BoardroomItem {
  const at = item.tags.findIndex(t => t.id === tag.id);
  const tags = at >= 0 ? item.tags.map((t, i) => (i === at ? tag : t)) : [...item.tags, tag];
  return { ...item, tags };
}

function bumpFacet(room: BoardroomDetail, tag: BoardroomTag): BoardroomDetail {
  const bucket = room.tag_facets[tag.dimension] ?? [];
  const at = bucket.findIndex(v => v.value === tag.value);
  const next =
    at >= 0
      ? bucket.map((v, i) => (i === at ? { ...v, count: v.count + 1 } : v))
      : [...bucket, { value: tag.value, count: 1 }];
  return { ...room, tag_facets: { ...room.tag_facets, [tag.dimension]: next } };
}

function replacePersona(list: BoardroomPersona[], p: BoardroomPersona): BoardroomPersona[] {
  const at = list.findIndex(x => x.id === p.id);
  return at >= 0 ? list.map((x, i) => (i === at ? p : x)) : [...list, p];
}

/**
 * Fold one event into the room. `view` decides whether an item belongs in the
 * feed being shown; counts are bumped regardless, because the rail describes
 * the whole room.
 */
export function applyEvent(
  state: RoomState,
  e: BoardroomWebSocketEvent,
  view: View,
  /** False: update rows already shown, never add one. For a feed narrowed by
   *  something an event cannot tell (what is waiting to be sorted). */
  insert = true,
): RoomState {
  if (e.boardroom_id !== state.room.id) return state;
  let { room, items } = state;

  if (e.status === 'item' && e.item) {
    const isNew = room.last_item_id === null || e.item.id > room.last_item_id;
    if (isNew) room = bumpCounts(room, e.item);
    const shown = items.some(i => i.id === e.item!.id);
    if (matchesView(e.item, view) && (insert || shown)) items = upsertItem(items, e.item);
    else if (items.some(i => i.id === e.item!.id)) items = items.filter(i => i.id !== e.item!.id);
    // A VERDICT is about its parent: stamp the ruling on the parent row too, so
    // the badge appears without a refetch. The server row would say the same.
    if (e.item.kind === 'VERDICT' && e.item.parent_ids.length) {
      const v = e.item.payload.verdict;
      const ref = {
        verdict: (typeof v === 'string' ? v : null) as BoardroomVerdictRef['verdict'],
        item_id: e.item.id,
        by: e.item.created_by_persona_id,
      };
      items = items.map(i =>
        e.item!.parent_ids.includes(i.id) && (!i.verdict || i.verdict.item_id <= ref.item_id)
          ? { ...i, verdict: ref }
          : i,
      );
    }
  } else if (e.status === 'tag' && e.tag) {
    const tag = e.tag;
    // Approval re-sends the same tag with approved_at set; only a fresh tag is a
    // new facet.
    if (!tag.approved_at) room = bumpFacet(room, tag);
    items = items.map(i => (i.id === tag.item_id ? attachTag(i, tag) : i));
  } else if (e.status === 'run' && e.run) {
    const r = e.run;
    // A run event is about ONE run. The old run's "finished" can land after the
    // next run was started (the POST response beat the socket), and clearing
    // the active run on it left the page showing Dig buttons for a room whose
    // server-side run was going — the next press got "already has a run going".
    const current = room.run;
    let next = current;
    if (r.status === 'RUNNING') {
      if (!current || current.id <= r.id) next = r;
    } else if (current && current.id === r.id) {
      next = null;
    }
    room = { ...room, run: next, tokens_spent: Math.max(room.tokens_spent, e.tokens_spent ?? 0) };
  } else if (e.status === 'persona' && e.persona) {
    room = { ...room, personas: replacePersona(room.personas, e.persona) };
  } else if (e.status === 'frame' && e.frame) {
    // A version, a proposal or an acceptance. Proposals wait; anything else is
    // the current frame from now on.
    const f = e.frame;
    const history = [f, ...(room.frame_history ?? []).filter(x => x.id !== f.id)];
    const proposals = f.proposed
      ? [f, ...(room.frame_proposals ?? []).filter(x => x.id !== f.id)]
      : (room.frame_proposals ?? []).filter(x => x.id !== f.id);
    room = { ...room, frame: f.proposed ? room.frame : f, frame_proposals: proposals, frame_history: history };
  } else if (e.status === 'lead' && e.lead) {
    // The room started following a lead, went back, or its leads changed. The
    // answer comes with it: following a lead, the answer is the lead's.
    room = { ...room, ...e.lead };
  } else if (e.status === 'answer' && e.answer) {
    // A new version of the room's answer.
    //
    // A KEEP DOES NOT BECOME CURRENT. A pass that read the board and found the
    // answer still standing writes a row too — with a fresh id and a fresh
    // timestamp, but the same answer. Swapping it in would reset every "since"
    // on screen and flicker a line that did not move, which is precisely the
    // thing the reader is watching for. It still joins the history: the room
    // checked, and that is worth keeping.
    const a = e.answer;
    const history = [a, ...(room.answer_history ?? []).filter(x => x.id !== a.id)];
    const current = a.unchanged || a.dismissed_at ? room.answer : a;
    room = { ...room, answer: current ?? null, answer_history: history, answering: e.answering ?? room.answering };
  } else if (e.status === 'tagging' && e.tagging) {
    // Counters only: the per-tag events are what move the items.
    room = { ...room, tagging: e.tagging };
  } else if (e.status === 'director') {
    // A round landed. The run carries the note; the worklists replace the
    // ones in play, because a round supersedes the last one rather than
    // adding to it.
    room = {
      ...room,
      run: e.run ?? room.run,
      assignments: e.assignments ?? room.assignments,
    };
  }
  if (room === state.room && items === state.items) return state;
  return { room, items };
}
